import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Matrix4,
  Ray,
  Vector3,
  type Vector3Tuple,
} from "three";
import { getTriangleHitPointInfo, MeshBVH } from "three-mesh-bvh";
import { isPlainObject } from "es-toolkit";

export type ClashKind = "penetration" | "clearance" | "uncertain";
export type ClashSettings = {
  tolerance: number;
  clearance: number;
  resolution: number;
};
export type ClashEvidence = {
  kind: ClashKind;
  distance: number;
  reason?: string;
  surfacePoint?: Vector3Tuple;
  method?: "surface-crossing";
  supportRadius?: number;
};
export type ClashCandidate = {
  id: string;
  componentId: number;
  kind: ClashKind;
  position: Vector3Tuple;
  bounds: [Vector3Tuple, Vector3Tuple];
  distance: number;
  support: number;
  samples?: Vector3Tuple[];
  samplingSpacing?: number;
  groupingRadius?: number;
  witness?: { scan: Vector3Tuple; cad: Vector3Tuple };
  reason?: string;
  status: "open" | "dismissed" | "annotated";
  method?: "surface-crossing";
  annotationId?: string;
  annotationFailure?: string;
  reviewGroupId?: string;
};
export type ClashRun = {
  fingerprint: string;
  settings: ClashSettings;
  hiddenScans: number[];
  candidates: ClashCandidate[];
  coverage: "complete" | "partial";
  reasons: string[];
  points: number;
  /**
   * Maps candidate bounds from their computation frame into the current frame.
   * Kept separately so container transforms never re-box the stored bounds.
   */
  boundsFrame?: number[];
  processing?: {
    nodes: number;
    failedNodes: number;
    completedTiles: number;
    unresolvedTiles: number;
    complete: boolean;
    usedCells: Array<[Vector3Tuple, Vector3Tuple]>;
    frame?: number[];
  };
};

/**
 * @param candidate Observed clash extent in its computation frame.
 * @param frame The owning run's `boundsFrame`.
 * @returns Axis-aligned bounds in the current frame.
 */
export function clashCandidateBounds(
  candidate: ClashCandidate,
  frame?: number[],
): Box3 {
  const bounds = new Box3(
    new Vector3().fromArray(candidate.bounds[0]),
    new Vector3().fromArray(candidate.bounds[1]),
  );
  return frame ? bounds.applyMatrix4(new Matrix4().fromArray(frame)) : bounds;
}

/**
 * @param candidate Evidence whose distance is displayed or exported.
 * @returns A label distinguishing surface intersections from solid depth.
 */
export function clashDistanceLabel(candidate: ClashCandidate): string {
  if (candidate.method === "surface-crossing") {
    return "Sample-to-crossing distance";
  }
  return candidate.kind === "penetration"
    ? "Max. inward distance"
    : "Min. surface gap";
}

const EPSILON = 0.000001;
export const CLASH_LIMITS = {
  cells: 100_000,
  edges: 250_000,
  minimumBand: 0.02,
  candidates: 1000,
  groupingRadius: 0.05,
  minimumSupport: 3,
  neighborChecks: 4_000_000,
  samples: 64,
  spacingMultiplier: 1.75,
  crossingAngleCosine: Math.cos(Math.PI / 6),
  crossingSeedRadius: 1.5,
};

/**
 * @param left Supported finding to compare.
 * @param right Supported finding to compare.
 * @returns Stable priority by kind, occupied support and distance.
 */
export function compareClashCandidates(
  left: ClashCandidate,
  right: ClashCandidate,
): number {
  return (
    Number(right.kind === "penetration") -
      Number(left.kind === "penetration") ||
    right.support - left.support ||
    (left.kind === "clearance"
      ? left.distance - right.distance
      : right.distance - left.distance) ||
    left.componentId - right.componentId ||
    Number(left.id > right.id) - Number(left.id < right.id)
  );
}

const DIRECTIONS = [
  new Vector3(1, Math.SQRT2, Math.sqrt(3)),
  new Vector3(Math.sqrt(3), -1, Math.SQRT2),
  new Vector3(Math.SQRT2, Math.sqrt(3), -1),
].map((direction) => direction.normalize());

function closedBoundary(positions: Float32Array): boolean {
  const edges = new Map<string, { count: number; winding: number }>();
  for (let offset = 0; offset < positions.length; offset += 9) {
    const vertices = [0, 3, 6].map((corner) =>
      [0, 1, 2]
        .map((axis) => Math.round(positions[offset + corner + axis] / EPSILON))
        .join(","),
    );
    if (new Set(vertices).size !== 3) return false;
    for (let corner = 0; corner < 3; corner++) {
      const start = vertices[corner];
      const end = vertices[(corner + 1) % 3];
      const forward = start < end;
      const key = forward ? `${start}/${end}` : `${end}/${start}`;
      const edge = edges.get(key) ?? { count: 0, winding: 0 };
      edge.count++;
      edge.winding += forward ? 1 : -1;
      if (edge.count > 2) return false;
      edges.set(key, edge);
      if (edges.size > CLASH_LIMITS.edges) return false;
    }
  }
  return (
    edges.size > 0 &&
    [...edges.values()].every((edge) => edge.count === 2 && edge.winding === 0)
  );
}

/**
 *
 */
export class CadClashClassifier {
  readonly geometry = new BufferGeometry();
  readonly bounds = new Box3();
  readonly closed: boolean;
  readonly bvh: MeshBVH;

  /**
   *
   * @param positions Owned uncut component triangles, relative to the analysis origin.
   */
  constructor(positions: Float32Array) {
    this.closed = closedBoundary(positions);
    this.geometry.setAttribute("position", new BufferAttribute(positions, 3));
    this.bvh = new MeshBVH(this.geometry, { maxDepth: 30, maxLeafTris: 8 });
    this.bvh.getBoundingBox(this.bounds);
  }

  /**
   *
   * @param point Point in the component's recentered frame.
   * @param settings Match tolerance and optional clearance in meters.
   * @param onSurfaceDistance Optional scan-patch collector, before tolerance suppression.
   * @returns Potential obstruction evidence, or undefined for a surface match or clear sample.
   */
  classify(
    point: Vector3,
    settings: ClashSettings,
    onSurfaceDistance?: (distance: number) => void,
  ): ClashEvidence | undefined {
    if (!this.closed && !onSurfaceDistance) return;
    const hit = this.bvh.closestPointToPoint(point);
    if (hit) onSurfaceDistance?.(hit.distance);
    if (!this.closed || !hit || hit.distance <= settings.tolerance) return;
    if (this.bounds.containsPoint(point)) {
      const votes = DIRECTIONS.map((direction) => {
        const hits = this.bvh
          .raycast(new Ray(point, direction), DoubleSide)
          .sort((left, right) => left.distance - right.distance);
        let count = 0;
        let previous = -Infinity;
        for (const intersection of hits) {
          if (intersection.distance - previous > EPSILON) count++;
          previous = intersection.distance;
        }
        return count % 2;
      });
      if (votes.every((vote) => vote === 1)) {
        return {
          kind: "penetration",
          distance: hit.distance,
          surfacePoint: hit.point.toArray(),
        };
      }
      if (votes.some((vote) => vote === 1)) {
        return {
          kind: "uncertain",
          distance: hit.distance,
          reason: "Ambiguous CAD containment",
        };
      }
    }
    if (hit.distance <= settings.clearance) {
      return {
        kind: "clearance",
        distance: hit.distance,
        surfacePoint: hit.point.toArray(),
      };
    }
  }

  /**
   * @param point Observed sample near a supported local scan patch.
   * @param samples Nearby coplanar scan samples, not inferred CAD caps.
   * @param normal Locally fitted scan normal.
   * @param settings Match tolerance and requested sampling resolution.
   * @param budget Shared bound on surface queries and neighbourhood connectivity checks.
   * @returns Supported transverse surface intersection, independently of solid closure.
   */
  crossing(
    point: Vector3,
    samples: Vector3[],
    normal: Vector3,
    settings: ClashSettings,
    budget?: { remaining: number },
  ): ClashEvidence | undefined {
    if (budget && budget.remaining-- <= 0) return;
    const nearest = this.bvh.closestPointToPoint(
      point,
      undefined,
      0,
      settings.resolution * CLASH_LIMITS.crossingSeedRadius,
    );
    if (!nearest) return;
    const { face } = getTriangleHitPointInfo(
      nearest.point,
      this.geometry,
      nearest.faceIndex,
    );
    if (Math.abs(face.normal.dot(normal)) >= CLASH_LIMITS.crossingAngleCosine) {
      return;
    }
    const seed = samples.findIndex((sample) => sample.equals(point));
    if (seed < 0) return;
    const connected = [seed];
    const visited = new Set(connected);
    for (const index of connected) {
      for (let neighbor = 0; neighbor < samples.length; neighbor++) {
        if (visited.has(neighbor)) continue;
        if (budget && budget.remaining-- <= 0) return;
        if (
          samples[index].distanceToSquared(samples[neighbor]) <=
          (settings.resolution * 3) ** 2
        ) {
          visited.add(neighbor);
          connected.push(neighbor);
        }
      }
    }
    const projected = connected
      .map((index) => ({
        sample: samples[index],
        distance: samples[index].clone().sub(nearest.point).dot(face.normal),
      }))
      .sort((left, right) => left.distance - right.distance);
    const margin = Math.max(settings.resolution, settings.tolerance);
    if (
      projected.filter(({ distance }) => distance < -margin).length <
        CLASH_LIMITS.minimumSupport ||
      projected.filter(({ distance }) => distance > margin).length <
        CLASH_LIMITS.minimumSupport
    ) {
      return;
    }
    const left = projected.findIndex(({ distance }) => distance >= -margin);
    const right = projected.findIndex(({ distance }) => distance > margin);
    for (let index = Math.max(1, left); index <= right; index++) {
      if (
        projected[index].distance - projected[index - 1].distance >
        settings.resolution * 3
      ) {
        return;
      }
    }
    const start = projected[Math.max(0, left - 1)].sample;
    const end = projected[right].sample;
    const direction = end.clone().sub(start);
    const length = direction.length();
    const hit = this.bvh
      .raycast(
        new Ray(start, direction.normalize()),
        DoubleSide,
        EPSILON,
        length - EPSILON,
      )
      .sort((leftHit, rightHit) => leftHit.distance - rightHit.distance)
      .at(0);
    if (
      !hit?.face ||
      Math.abs(hit.face.normal.dot(normal)) >= CLASH_LIMITS.crossingAngleCosine
    ) {
      return;
    }
    return {
      kind: "penetration",
      method: "surface-crossing",
      reason:
        "Locally supported scan surface crosses CAD; not a solid penetration-depth measurement",
      distance: point.distanceTo(hit.point),
      surfacePoint: hit.point.toArray(),
      supportRadius: Math.min(
        CLASH_LIMITS.groupingRadius,
        Math.max(...connected.map((index) => samples[index].distanceTo(point))),
      ),
    };
  }

  /**
   *
   */
  dispose(): void {
    this.geometry.dispose();
  }
}

/**
 *
 */
export class ClashAccumulator {
  private readonly cells = new Map<
    string,
    { point: Vector3; evidence: ClashEvidence; spacing?: number; bounds: Box3 }
  >();
  limited = false;
  clusteringLimited = false;
  neighborChecks = 0;
  insufficientSupport = 0;

  /**
   *
   * @param componentId Source CAD object identity.
   * @param resolution Occupied-cell edge size in meters.
   */
  constructor(
    readonly componentId: number,
    readonly resolution: number,
  ) {}

  /** @returns Number of independently occupied evidence cells. */
  get size(): number {
    return this.cells.size;
  }

  /**
   *
   * @param point Observed sample in recentered coordinates.
   * @param evidence Classification of that sample against this component.
   * @param spacing Nominal source sampling spacing in meters, when available.
   */
  add(point: Vector3, evidence: ClashEvidence, spacing?: number): void {
    if (evidence.kind === "uncertain") return;
    const sampleSpacing =
      spacing !== undefined && Number.isFinite(spacing) && spacing > 0
        ? spacing
        : undefined;
    const coordinates = point
      .toArray()
      .map((value) => Math.floor(value / this.resolution));
    const key = `${evidence.method ?? evidence.kind}:${coordinates.join(",")}`;
    const existing = this.cells.get(key);
    if (existing) {
      const previous = existing.point.toArray();
      const values = point.toArray();
      const axis = values.findIndex(
        (value, index) => value !== previous[index],
      );
      const stronger =
        evidence.kind === "clearance"
          ? evidence.distance < existing.evidence.distance
          : evidence.distance > existing.evidence.distance;
      if (
        stronger ||
        (evidence.distance === existing.evidence.distance &&
          axis >= 0 &&
          values[axis] < previous[axis])
      ) {
        existing.point.copy(point);
        existing.evidence = { ...evidence };
      }
      existing.bounds.expandByPoint(point);
      if (sampleSpacing !== undefined) {
        existing.spacing = Math.min(
          existing.spacing ?? Infinity,
          sampleSpacing,
        );
      }
      return;
    }
    if (this.cells.size >= CLASH_LIMITS.cells) {
      this.limited = true;
      return;
    }
    this.cells.set(key, {
      point: point.clone(),
      evidence: { ...evidence },
      spacing: sampleSpacing,
      bounds: new Box3(point.clone(), point.clone()),
    });
  }

  /**
   *
   * @param origin World origin to restore on serialized candidates.
   * @param maxChecks Remaining shared neighbourhood-query budget.
   * @param core Optional owned work region; neighbouring samples establish connectivity only.
   * @param onGroup Consumer of owned findings and shared density-core identities.
   * @returns Density-connected candidate groups with deterministic identifiers.
   */
  finish(
    origin: Vector3,
    maxChecks = CLASH_LIMITS.neighborChecks,
    core?: Box3,
    onGroup?: (candidate: ClashCandidate, connections: string[]) => void,
  ): ClashCandidate[] {
    this.insufficientSupport = 0;
    this.neighborChecks = 0;
    this.clusteringLimited = false;
    const cells = [...this.cells.entries()]
      .sort(([left], [right]) => Number(left > right) - Number(left < right))
      .map(([key, cell]) => ({
        ...cell,
        key,
        radius: Math.min(
          CLASH_LIMITS.groupingRadius,
          Math.max(
            Math.max(this.resolution, cell.spacing ?? this.resolution) *
              CLASH_LIMITS.spacingMultiplier,
            cell.evidence.supportRadius ?? 0,
          ),
        ),
      }));
    const levels = new Map<number, Map<string, number[]>>();
    for (const [index, cell] of cells.entries()) {
      const edge =
        this.resolution *
        2 ** Math.ceil(Math.log2(cell.radius / this.resolution));
      let buckets = levels.get(edge);
      if (!buckets) {
        buckets = new Map();
        levels.set(edge, buckets);
      }
      const key = `${cell.evidence.method ?? cell.evidence.kind}:${cell.point
        .toArray()
        .map((value) => Math.floor(value / edge))
        .join(",")}`;
      const bucket = buckets.get(key);
      if (bucket) bucket.push(index);
      else buckets.set(key, [index]);
    }
    const neighborhoods = (index: number): number[] | undefined => {
      const cell = cells[index];
      const neighbors: number[] = [];
      for (const [edge, buckets] of levels) {
        const radius = Math.min(cell.radius, edge);
        const minimum = cell.point
          .toArray()
          .map((value) => Math.floor((value - radius) / edge));
        const maximum = cell.point
          .toArray()
          .map((value) => Math.floor((value + radius) / edge));
        for (let axisX = minimum[0]; axisX <= maximum[0]; axisX++) {
          for (let axisY = minimum[1]; axisY <= maximum[1]; axisY++) {
            for (let axisZ = minimum[2]; axisZ <= maximum[2]; axisZ++) {
              for (const neighbor of buckets.get(
                `${cell.evidence.method ?? cell.evidence.kind}:${axisX},${axisY},${axisZ}`,
              ) ?? []) {
                if (this.neighborChecks >= maxChecks) {
                  this.clusteringLimited = true;
                  return;
                }
                this.neighborChecks++;
                const other = cells[neighbor];
                if (
                  cell.point.distanceToSquared(other.point) <=
                  Math.min(cell.radius, other.radius) ** 2
                ) {
                  neighbors.push(neighbor);
                }
              }
            }
          }
        }
      }
      return neighbors.sort((left, right) => left - right);
    };
    const visited = new Uint8Array(cells.length);
    const assigned = new Uint8Array(cells.length);
    const densityCore = new Uint8Array(cells.length);
    const candidates: ClashCandidate[] = [];
    for (let index = 0; index < cells.length; index++) {
      if (visited[index]) continue;
      visited[index] = 1;
      const neighbors = neighborhoods(index);
      if (!neighbors) break;
      if (neighbors.length < CLASH_LIMITS.minimumSupport) continue;
      densityCore[index] = 1;
      const group: number[] = [];
      function append(indices: number[]): void {
        for (const neighbor of indices) {
          if (!assigned[neighbor]) {
            assigned[neighbor] = 1;
            group.push(neighbor);
          }
        }
      }
      append(neighbors);
      let complete = true;
      for (const member of group) {
        if (visited[member]) continue;
        visited[member] = 1;
        const adjacent = neighborhoods(member);
        if (!adjacent) {
          complete = false;
          break;
        }
        if (adjacent.length >= CLASH_LIMITS.minimumSupport) {
          densityCore[member] = 1;
          append(adjacent);
        }
      }
      if (!complete) break;
      group.sort((left, right) => left - right);
      const connections = group
        .filter((member) => densityCore[member])
        .map((member) => `${this.componentId}:${cells[member].key}`);
      const owned = core
        ? group.filter((member) => {
            const { point } = cells[member];
            return (
              core.containsPoint(point) &&
              point.x < core.max.x &&
              point.y < core.max.y &&
              point.z < core.max.z
            );
          })
        : [...group];
      if (!owned.length) continue;
      group.splice(0, group.length, ...owned);
      const seed = cells[owned[0]];
      const bounds = new Box3();
      let { distance } = seed.evidence;
      let extreme = owned[0];
      for (const member of group) {
        const cell = cells[member];
        const { distance: sampleDistance } = cell.evidence;
        bounds.union(cell.bounds);
        if (
          cell.evidence.kind === "clearance"
            ? sampleDistance < distance
            : sampleDistance > distance
        ) {
          distance = sampleDistance;
          extreme = member;
        }
      }
      const center = bounds.getCenter(new Vector3());
      const representative = group.reduce(
        (nearest, member) =>
          cells[member].point.distanceToSquared(center) <
          nearest.point.distanceToSquared(center)
            ? cells[member]
            : nearest,
        seed,
      );
      const sampleCount = Math.min(CLASH_LIMITS.samples - 2, group.length);
      const samples = new Set([
        representative,
        cells[extreme],
        ...Array.from(
          { length: sampleCount },
          (_, sample) =>
            cells[group[Math.floor((sample * group.length) / sampleCount)]],
        ),
      ]);
      const { surfacePoint } = cells[extreme].evidence;
      bounds.translate(origin);
      const candidate: ClashCandidate = {
        id: `${this.componentId}:${seed.key}`,
        componentId: this.componentId,
        kind: seed.evidence.kind,
        method: seed.evidence.method,
        position: representative.point.clone().add(origin).toArray(),
        bounds: [bounds.min.toArray(), bounds.max.toArray()],
        distance,
        support: group.length,
        samples: [...samples].map((cell) =>
          cell.point.clone().add(origin).toArray(),
        ),
        samplingSpacing: group.every(
          (member) => cells[member].spacing !== undefined,
        )
          ? group.reduce(
              (maximum, member) =>
                Math.max(maximum, cells[member].spacing ?? 0),
              0,
            )
          : undefined,
        groupingRadius: group.reduce(
          (maximum, member) => Math.max(maximum, cells[member].radius),
          0,
        ),
        witness: surfacePoint
          ? {
              scan: cells[extreme].point.clone().add(origin).toArray(),
              cad: new Vector3().fromArray(surfacePoint).add(origin).toArray(),
            }
          : undefined,
        reason: seed.evidence.reason,
        status: "open",
      };
      candidates.push(candidate);
      onGroup?.(candidate, connections);
    }
    this.insufficientSupport = assigned.reduce(
      (count, value, index) =>
        count +
        Number(
          !value &&
            (!core ||
              (core.containsPoint(cells[index].point) &&
                cells[index].point.x < core.max.x &&
                cells[index].point.y < core.max.y &&
                cells[index].point.z < core.max.z)),
        ),
      0,
    );
    this.cells.clear();
    return candidates.sort(compareClashCandidates);
  }
}

/**
 *
 * @param value Untrusted saved result.
 * @returns Whether the result satisfies the bounded clash schema.
 */
export function isClashRun(value: unknown): value is ClashRun {
  function nonnegative(item: unknown): item is number {
    return typeof item === "number" && Number.isFinite(item) && item >= 0;
  }
  function tuple(item: unknown): boolean {
    return (
      Array.isArray(item) &&
      item.length === 3 &&
      item.every(
        (coordinate) =>
          typeof coordinate === "number" && Number.isFinite(coordinate),
      )
    );
  }
  if (!isPlainObject(value) || !isPlainObject(value.settings)) return false;
  return (
    typeof value.fingerprint === "string" &&
    value.fingerprint.length <= 256 &&
    nonnegative(value.settings.tolerance) &&
    nonnegative(value.settings.clearance) &&
    nonnegative(value.settings.resolution) &&
    value.settings.resolution > 0 &&
    (value.coverage === "complete" || value.coverage === "partial") &&
    nonnegative(value.points) &&
    (value.boundsFrame === undefined ||
      (Array.isArray(value.boundsFrame) &&
        value.boundsFrame.length === 16 &&
        value.boundsFrame.every(Number.isFinite))) &&
    (value.processing === undefined ||
      (isPlainObject(value.processing) &&
        nonnegative(value.processing.nodes) &&
        nonnegative(value.processing.failedNodes) &&
        nonnegative(value.processing.completedTiles) &&
        nonnegative(value.processing.unresolvedTiles) &&
        typeof value.processing.complete === "boolean" &&
        Array.isArray(value.processing.usedCells) &&
        value.processing.usedCells.every(
          (bounds) =>
            Array.isArray(bounds) && bounds.length === 2 && bounds.every(tuple),
        ) &&
        (value.processing.frame === undefined ||
          (Array.isArray(value.processing.frame) &&
            value.processing.frame.length === 16 &&
            value.processing.frame.every(Number.isFinite))))) &&
    Array.isArray(value.hiddenScans) &&
    value.hiddenScans.length <= CLASH_LIMITS.cells &&
    value.hiddenScans.every(Number.isInteger) &&
    Array.isArray(value.reasons) &&
    value.reasons.length <= 100 &&
    value.reasons.every(
      (reason) => typeof reason === "string" && reason.length <= 1000,
    ) &&
    Array.isArray(value.candidates) &&
    value.candidates.every(
      (candidate) =>
        isPlainObject(candidate) &&
        typeof candidate.id === "string" &&
        candidate.id.length <= 256 &&
        Number.isInteger(candidate.componentId) &&
        ["penetration", "clearance", "uncertain"].includes(candidate.kind) &&
        ["open", "dismissed", "annotated"].includes(candidate.status) &&
        tuple(candidate.position) &&
        Array.isArray(candidate.bounds) &&
        candidate.bounds.length === 2 &&
        candidate.bounds.every(tuple) &&
        nonnegative(candidate.distance) &&
        nonnegative(candidate.support) &&
        (candidate.method === undefined ||
          (candidate.method === "surface-crossing" &&
            candidate.kind === "penetration")) &&
        (candidate.samples === undefined ||
          (Array.isArray(candidate.samples) &&
            candidate.samples.length <= CLASH_LIMITS.samples &&
            candidate.samples.every(tuple))) &&
        (candidate.samplingSpacing === undefined ||
          (nonnegative(candidate.samplingSpacing) &&
            candidate.samplingSpacing > 0)) &&
        (candidate.groupingRadius === undefined ||
          (nonnegative(candidate.groupingRadius) &&
            candidate.groupingRadius > 0)) &&
        (candidate.witness === undefined ||
          (isPlainObject(candidate.witness) &&
            tuple(candidate.witness.scan) &&
            tuple(candidate.witness.cad))) &&
        (candidate.reason === undefined ||
          (typeof candidate.reason === "string" &&
            candidate.reason.length <= 1000)) &&
        (candidate.annotationId === undefined ||
          (typeof candidate.annotationId === "string" &&
            candidate.annotationId.length <= 256)) &&
        (candidate.reviewGroupId === undefined ||
          (typeof candidate.reviewGroupId === "string" &&
            candidate.reviewGroupId.length <= 256)) &&
        (candidate.annotationFailure === undefined ||
          (typeof candidate.annotationFailure === "string" &&
            candidate.annotationFailure.length <= 1000)) &&
        (candidate.status !== "annotated" ||
          typeof candidate.annotationId === "string"),
    )
  );
}
